// SEO Configuration for Siak Studios
export const SEO_CONFIG = {
  siteUrl: 'https://siak.in',
  siteName: 'Siak Studios',
  defaultLocale: 'en_US',
  twitterHandle: '@siakstudios',
  defaultImage: {
    url: '/og-image.png',
    width: 1200,
    height: 630,
    alt: 'Siak Studios - Creative Production House',
  },
} as const;

export type SEOConfig = {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: {
    url: string;
    width: number;
    height: number;
    alt: string;
  };
  schema?: Record<string, any>;
  noindex?: boolean;
  keywords?: string[];
};

// Page-specific SEO configurations
export const PAGE_SEO: Record<string, SEOConfig> = {
  home: {
    title: 'Siak Studios — Creative Production House | App Development & Design',
    description: 'Independent creative studio specializing in mobile app development, web design, and digital experiences. 50+ projects completed with 100% client satisfaction. Quality-focused, remote-first production house.',
    canonical: '/',
    keywords: [
      'app development',
      'web design',
      'branding',
      'logo design',
      'UI/UX design',
      'mobile apps',
      'desktop apps',
      'digital products',
      'creative studio',
      'remote development',
      'react development',
      'privacy-first apps',
    ],
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Siak Studios',
        url: 'https://siak.in',
        logo: 'https://siak.in/logo.png',
        description: 'Independent creative production house specializing in mobile app development, web design, and digital experiences.',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          reviewCount: '50',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Siak Studios',
        url: 'https://siak.in',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://siak.in/work?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  },
  work: {
    title: 'Our Work — Portfolio | Siak Studios',
    description: 'Explore our portfolio of 50+ projects including mobile apps, web platforms, and brand identities. See how we transform ideas into polished, performant digital products.',
    canonical: '/work',
    keywords: [
      'portfolio',
      'case studies',
      'app portfolio',
      'web design portfolio',
      'branding portfolio',
      'mobile app examples',
      'web design examples',
    ],
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Our Work - Portfolio',
      description: 'Portfolio of apps, websites, and brand identities created by Siak Studios',
      url: 'https://siak.in/work',
    },
  },
  services: {
    title: 'Our Services — App Development, Web Design & Branding | Siak Studios',
    description: 'Comprehensive digital product services: mobile app development, web design, and brand identity. Our 4-phase process ensures quality from discovery to delivery. Expert UI/UX and privacy-first design.',
    canonical: '/services',
    keywords: [
      'app development services',
      'web design services',
      'branding services',
      'logo design services',
      'UI/UX design services',
      'mobile app development',
      'web development',
      'reddit design',
      'brand identity',
    ],
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Digital Product Development',
        provider: {
          '@type': 'Organization',
          name: 'Siak Studios',
          url: 'https://siak.in',
        },
        areaServed: 'Worldwide',
        description: 'End-to-end creative production from concept to delivery, specializing in mobile apps, web platforms, and brand identity.',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Digital Services',
          itemListElement: [
            { '@type': 'Service', name: 'Mobile App Development', description: 'Cross-platform mobile apps for Android and iOS using React Native and Flutter.' },
            { '@type': 'Service', name: 'Web Design & Development', description: 'High-performance web applications and responsive marketing sites.' },
            { '@type': 'Service', name: 'Brand Identity Design', description: 'Complete brand systems including logos, typography, and visual guidelines.' },
            { '@type': 'Service', name: 'UI/UX Design', description: 'User-centered design focusing on clarity, accessibility, and delight.' },
            { '@type': 'Service', name: 'Reddit Branding', description: 'Specialized visual identity and asset creation for Reddit communities and brands.' },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            'name': 'What is your development process?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Our proven four-phase process includes: 1. Discover (research and audience understanding), 2. Define (creative direction), 3. Create (build and iteration), and 4. Deliver (production-ready assets and guidelines).',
            },
          },
          {
            '@type': 'Question',
            'name': 'Do you provide post-launch support?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes, every project includes a post-launch support period. We also offer ongoing maintenance packages for bug fixes, performance updates, and new feature development.',
            },
          },
        ],
      },
    ],
  },
  about: {
    title: 'About Us — Our Story & Values | Siak Studios',
    description: 'Learn about Siak Studios - a quality-focused team building digital products with obsessive attention to craft and privacy-by-design. Based on years of experience in app development.',
    canonical: '/about',
    keywords: [
      'about siak studios',
      'creative studio team',
      'app development team',
      'design studio values',
      'privacy-first development',
      'remote studio',
    ],
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Siak Studios',
      description: 'Our story, values, and approach to building digital products',
      url: 'https://siak.in/about',
      mainEntity: {
        '@type': 'Organization',
        name: 'Siak Studios',
        description: 'Independent creative production house with a focus on privacy-first app development.',
      },
    },
  },
  journal: {
    title: 'Journal — Insights & Updates | Siak Studios',
    description: 'Expert insights on app development, design trends, and privacy-first technology. Stay updated with the latest from Siak Studios.',
    canonical: '/journal',
    keywords: [
      'app development blog',
      'design insights',
      'tech blog',
      'development insights',
      'design trends',
      'privacy technology',
    ],
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Siak Studios Journal',
      description: 'Insights and updates from Siak Studios',
      url: 'https://siak.in/journal',
      publisher: {
        '@type': 'Organization',
        name: 'Siak Studios',
      },
    },
  },
  contact: {
    title: 'Contact Us — Start Your Project | Siak Studios',
    description: 'Get in touch to discuss your app, website, or branding project. We respond within 24 hours. remote-first studio delivering worldwide.',
    canonical: '/contact',
    keywords: [
      'contact siak studios',
      'hire app developer',
      'hire web designer',
      'app development quote',
      'web design quote',
      'branding quote',
    ],
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact Siak Studios',
        description: 'Get in touch to start your project',
        url: 'https://siak.in/contact',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How long does it take to build an app?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Most mobile apps take 4-8 weeks from concept to Play Store launch, depending on complexity and features.',
            },
          },
          {
            '@type': 'Question',
            name: 'Do you work with clients remotely?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutely. Most of our clients are remote. We use video calls and asynchronous communication to keep projects moving smoothly.',
            },
          },
        ],
      },
    ],
  },
  notFound: {
    title: 'Page Not Found — 404 | Siak Studios',
    description: 'The page you are looking for does not exist or has been moved. Return to our homepage to explore our services and portfolio.',
    canonical: '/404',
    noindex: true,
  },
  privacy: {
    title: 'Privacy Policy — Siak Studios',
    description: 'Our commitment to your privacy. Learn how we collect, use, and protect your information. Privacy-first approach to data handling.',
    canonical: '/privacy',
    keywords: ['privacy policy', 'data protection', 'privacy-first', 'data collection'],
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy',
      description: 'Privacy policy for Siak Studios',
      url: 'https://siak.in/privacy',
    },
  },
  terms: {
    title: 'Terms & Conditions — Siak Studios',
    description: 'Terms and conditions for using Siak Studios services. Understand our service agreements, responsibilities, and policies.',
    canonical: '/terms',
    keywords: ['terms and conditions', 'service agreement', 'terms of service', 'legal'],
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms & Conditions',
      description: 'Terms and conditions for Siak Studios services',
      url: 'https://siak.in/terms',
    },
  },
};
